import { QSelectItem } from '@qualio/ui-components';
import { removeUsersfromGroup } from '../api/medtech-api';
import { getContentHiddenWarningKey, getErrorCode } from '../errors/um-errors';
import { Group } from '../types';

/**
 * @description Formats MTBE groups to QSelectItems
 */
export const formatGroupsToSelectOptions = (groups: Group[]): QSelectItem[] => {
  return groups.map(({ id, name }) => {
    return {
      label: name.toUpperCase(),
      value: id.toString(),
    };
  });
};

/**
 * @description Removes the users from each group, one request per group, without forcing the removal.
 * Groups whose removal was rejected because it would hide content from its owners are returned, along
 * with the distinct warning i18n keys, so the caller can confirm and retry with forceRemove.
 * Any other error is rethrown.
 */
export const removeUsersFromGroupsUnlessContentHidden = async (groupIds: number[], userIds: number[]) => {
  const contentHiddenResults = (
    await Promise.all(
      groupIds.map(async (groupId) => {
        try {
          await removeUsersfromGroup({ groups: [groupId], users: userIds, forceRemove: false });
          return null;
        } catch (err) {
          const warningKey = getContentHiddenWarningKey(getErrorCode(err));
          if (warningKey) {
            return { groupId, warningKey };
          }
          throw err;
        }
      }),
    )
  ).filter((result): result is { groupId: number; warningKey: string } => result !== null);

  return {
    groupIdsNeedingForce: contentHiddenResults.map(({ groupId }) => groupId),
    warningKeys: Array.from(new Set(contentHiddenResults.map(({ warningKey }) => warningKey))),
  };
};
